import { observable, action, computed } from "mobx";
import { BaseListState, BaseListStore } from "./BaseListStore";

import { SimpleHorse, Horse } from "../model/Horse";
import { HorsesEndpoints } from "../endpoints/HorseEndpoints";
import { HorseRequest } from "../requests/HorseRequests";

import { ListWrapper, EmptyWrapper } from "../model/ListWrapper";

export interface HorseListFilters {
    fullTextSearch: string;
}

export interface HorseListState extends BaseListState {
    sortField?: "name";
}

export class HorseListStore extends BaseListStore<HorseListFilters, HorseListState, SimpleHorse> {
    @observable private _similarHorses: ListWrapper<SimpleHorse> = EmptyWrapper();
    private mostRecentSimilarHorsesRequest: number = 1;

    constructor(private horsesEndpoints: HorsesEndpoints) {
        super();
    }

    @action async refreshSimilarHorses(fullTextSearch: string): Promise<ListWrapper<SimpleHorse>> {
        this.setLoading();
        try {
            const requestNo = ++this.mostRecentSimilarHorsesRequest;
            const similarHorses = await this.horsesEndpoints.getHorseList(10, 0, undefined, { fullTextSearch });

            // Only update if this is the most recent request
            if (requestNo === this.mostRecentSimilarHorsesRequest) {
                this._similarHorses = similarHorses;
                this.setReady();
            }
            return this._similarHorses;
        } catch (e) {
            this.setReady();
            throw e;
        }
    }

    /**
     *
     * @param filters
     * @deprecated Use updateListFilters instead
     */
    @action updateHorsesListFilters(filters: Partial<HorseListFilters>) {
        this.updateListFilters(filters);
    }

    /**
     *
     * @param state
     * @deprecated Use updateListState instead
     */
    @action updateHorseListState(state: Partial<HorseListState>) {
        this.updateListState(state);
    }

    @action async addHorse(request: HorseRequest): Promise<Horse> {
        this.setLoading();
        try {
            const newHorse = await this.horsesEndpoints.createHorse(request);

            // Now that we've created a new Horse, let the store know that we should
            // refresh our list.
            this.refresh();

            return newHorse;
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    @action async deleteHorse(horseId: number): Promise<void> {
        this.setLoading();
        try {
            await this.horsesEndpoints.deleteHorse(horseId);
            this.refresh();
        } catch (e) {
            // Nothing to handle here, pass it back to the requester to show a message
            throw e;
        } finally {
            this.setReady();
        }
    }

    /**
     * @deprecated Use listFilters instead
     */
    @computed get horseListFilters(): HorseListFilters {
        return this.listFilters;
    }

    /**
     * @deprecated Use listState instead
     */
    @computed get horseListState(): HorseListState {
        return this.listState;
    }

    /**
     * @deprecated Use list instead
     */
    @computed get horseList(): ListWrapper<SimpleHorse> {
        return this.list;
    }

    @computed get similarHorses(): ListWrapper<SimpleHorse> {
        return this._similarHorses;
    }

    protected obtainList(): Promise<ListWrapper<SimpleHorse>> {
        const state = this._listState,
            filters = this._listFilters;
        return this.horsesEndpoints.getHorseList(
            state.sizePerPage,
            (state.page - 1) * state.sizePerPage,
            state.sortField && state.sortOrder ? `${state.sortField} ${state.sortOrder}` : undefined,
            {
                fullTextSearch: filters.fullTextSearch
            }
        );
    }

    protected defaultFilters(): HorseListFilters {
        return {
            fullTextSearch: ""
        };
    }

    protected defaultState(): HorseListState {
        // Nothing added here
        return super.defaultState();
    }
}



// WEBPACK FOOTER //
// ./src/stores/HorseListStore.ts